import React, { useState, useEffect, useRef } from "react";
import { message, Modal, Button } from 'antd';
import { useNavigate } from 'react-router-dom';
import { productAPI, API_BASE_URL, sourcingRequestAPI } from '../api';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { compressImage, isImageFile, formatFileSize } from '../utils/imageCompression';
import "./NewHome.css";

const featureCards = [
  {
    title: "Unified Fulfillment",
    description:
      "Keep warehousing, picking, packing, and last-mile delivery in sync so every order leaves on time and arrives as promised.",
    image: "/images/newhome/feature-fulfillment.svg",
  },
  {
    title: "Brand-Ready Customization",
    description:
      "Co-design packaging, logos, and product tweaks with our team so every shipment lands on-brand and made for your customers.",
    image: "/images/newhome/feature-customization.svg",
  },
  {
    title: "On-Demand Product Sourcing",
    description:
      "Tell us what you need and we’ll source, vet suppliers, and coordinate samples so you can launch new products with confidence.",
    image: "/images/newhome/feature-orders.svg",
  },
];

const storeOptions = [
  { value: "shopify", label: "Shopify" },
  { value: "woocommerce", label: "WooCommerce" },
  { value: "shopee", label: "Shopee" },
  { value: "amazon", label: "Amazon" },
  { value: "lazada", label: "Lazada" },
];

const testimonials = [
  {
    name: "User",
    role: "Cross-border Seller",
    quote:
      "CH Dropshipping lifted our fulfillment efficiency by 35%. Peak season used to mean chaos—now every parcel leaves on schedule.",
    avatar: "/images/newhome/avatar-user.svg",
  },
  {
    name: "Uak",
    role: "Brand Merchant",
    quote:
      "Automated inventory syncs and shipping workflows let us focus on storytelling and community. The operations headaches disappeared overnight.",
    avatar: "/images/newhome/avatar-uak.svg",
  },
];

const allowedAttachmentTypes = [
  'image/jpeg', 'image/png', 'image/gif', 'image/webp',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
];

const IMAGE_SIZE_LIMIT = 1024 * 1024; // 1MB for images
const DOCUMENT_SIZE_LIMIT = 3 * 1024 * 1024; // 3MB for other files
const ATTACHMENT_ACCEPT = '.jpg,.jpeg,.png,.gif,.webp,.pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx';
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const NewHome = () => {
  const navigate = useNavigate();
  const sourcingSectionRef = useRef(null);
  const [selectedStore, setSelectedStore] = useState(storeOptions[0].value);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sourcingEmail, setSourcingEmail] = useState('');
  const [sourcingMessage, setSourcingMessage] = useState('');
  const [sourcingFiles, setSourcingFiles] = useState([]);
  const [sourcingLoading, setSourcingLoading] = useState(false);
  const [sourcingSuccessVisible, setSourcingSuccessVisible] = useState(false);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await productAPI.getPublic({ page: 1, page_size: 8 });
      const fetchedProducts = response.data.data || [];
      setProducts(fetchedProducts);
    } catch (error) {

    } finally {
      setLoading(false);
    }
  };

  const getProductImage = (product) => {
    if (product.cover_picture && product.images) {
      const coverImage = product.images.find(img => img.id === product.cover_picture);
      if (coverImage) {
        return `${API_BASE_URL}/products/${product.id}/images/${coverImage.id}`;
      }
    }

    if (product.images && product.images.length > 0) {
      return `${API_BASE_URL}/products/${product.id}/images/${product.images[0].id}`;
    }

  };

  const getProductPrice = (product) => {
    if (!product.variants) {
      return { price: 0, currency: 'USD' };
    }

    if (product.variants.SKU !== undefined) {
      return {
        price: product.variants.price || 0,
        currency: product.variants.currency || 'USD'
      };
    }

    const allVariantPrices = [];

    const collectAllVariantPrices = (obj) => {
      if (obj && typeof obj === 'object') {
        if (obj.SKU !== undefined) {
          allVariantPrices.push({
            price: obj.price || 0,
            currency: obj.currency || 'USD'
          });
        } else {
          for (const value of Object.values(obj)) {
            collectAllVariantPrices(value);
          }
        }
      }
    };

    collectAllVariantPrices(product.variants);

    if (allVariantPrices.length === 0) {
      return { price: 0, currency: 'USD' };
    }

    const lowestPrice = Math.min(...allVariantPrices.map(v => v.price));
    const currency = allVariantPrices[0].currency || 'USD';

    return { price: lowestPrice, currency };
  };

  const handleSourcingFilesChange = (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) {
      return;
    }

    const nextFiles = [...sourcingFiles];

    files.forEach((file) => {
      if (!allowedAttachmentTypes.includes(file.type)) {
        message.error('File type not supported. Please upload images, PDF, DOC, DOCX, PPT, PPTX, XLS, or XLSX files.');
        return;
      }

      const isImage = file.type.startsWith('image/');
      const maxSize = isImage ? IMAGE_SIZE_LIMIT : DOCUMENT_SIZE_LIMIT;
      const sizeLabel = isImage ? '1MB' : '3MB';

      if (!isImage && file.size > maxSize) {
        message.error(`File ${file.name} exceeds ${sizeLabel}`);
        return;
      }

      if (isImage && file.size > IMAGE_SIZE_LIMIT) {
        message.warning(`Image ${file.name} exceeds 1MB, will be automatically compressed before upload`);
      }

      nextFiles.push({
        uid: `${file.name}-${Date.now()}-${Math.random()}`,
        file,
        name: file.name,
        size: file.size,
        type: file.type,
      });
    });

    setSourcingFiles(nextFiles);
    event.target.value = '';
  };

  const handleRemoveSourcingFile = (uid) => {
    setSourcingFiles((prev) => prev.filter((item) => item.uid !== uid));
  };

  const resetSourcingForm = () => {
    setSourcingEmail('');
    setSourcingMessage('');
    setSourcingFiles([]);
  };

  const handleSourcingSubmit = async (event) => {
    event.preventDefault();

    const trimmedEmail = sourcingEmail.trim();
    const trimmedMessage = sourcingMessage.trim();

    if (!trimmedEmail) {
      message.error('Please enter your email');
      return;
    }

    if (!EMAIL_REGEX.test(trimmedEmail)) {
      message.error('Please enter a valid email');
      return;
    }

    if (!trimmedMessage) {
      message.error('Please enter your sourcing request.');
      return;
    }

    setSourcingLoading(true);

    try {
      const processedFiles = [];
      for (const attachment of sourcingFiles) {
        const file = attachment.file;
        if (isImageFile(file) && file.size > IMAGE_SIZE_LIMIT) {
          message.loading(`Compressing image ${file.name}...`, 0);
          try {
            const compressedFile = await compressImage(file, 700);
            processedFiles.push(compressedFile);
            message.destroy();
            message.success(`Image ${file.name} compressed (${formatFileSize(file.size)} → ${formatFileSize(compressedFile.size)})`);
          } catch (error) {
            message.destroy();
            message.warning(`Image ${file.name} compression failed, using original file`);
            processedFiles.push(file);
          }
        } else {
          processedFiles.push(file);
        }
      }

      const formData = new FormData();
      formData.append('email', trimmedEmail);
      formData.append('message', trimmedMessage);
      processedFiles.forEach((file) => {
        formData.append('files', file);
      });

      await sourcingRequestAPI.submit(formData);
      message.success('Sourcing request submitted successfully');
      resetSourcingForm();
      setSourcingSuccessVisible(true);
    } catch (error) {
      const errorMessage = error?.response?.data?.detail || 'Failed to submit sourcing request';
      message.error(errorMessage);
    } finally {
      setSourcingLoading(false);
    }
  };

  const handleScrollToSourcing = () => {
    if (sourcingSectionRef.current) {
      sourcingSectionRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleGoToProducts = () => {
    navigate('/products');
  };

  const handleGoToRegister = () => {
    navigate('/register');
  };

  const handleGoToMyStores = () => {
    navigate('/dropshipper/my-stores');
  };

  return (
    <div className="newhome-page shared-background">
      <div className="newhome-inner">
        <Header />

        <section className="newhome-hero">
          <div className="newhome-hero-text">
            <span className="newhome-hero-badge">Are you looking for global dropshipping?</span>
            <h1 className="newhome-hero-title">Go worldwide with one connected operations hub</h1>
            <p className="newhome-hero-subtitle">
              CH Dropshipping unifies sourcing, customization, and delivery so expanding into new markets stays predictable and every parcel leaves on time.
            </p>
            <div className="newhome-hero-buttons">
              <button className="newhome-button" onClick={handleGoToProducts}>Start Now</button>
              <button className="newhome-button newhome-button--ghost" onClick={handleScrollToSourcing}>Talk to Sales</button>
            </div>
          </div>
          <div className="newhome-hero-illustration">
            <img src="/images/newhome/bg-hero.png" alt="Hero placeholder" />
          </div>
        </section>

        <section id="features">
          <h2 className="newhome-section-title">Everything You Need in One Platform</h2>
          <p className="newhome-section-subtitle">
            Bring essential operations into a single dashboard, automate the busywork, and give your team clarity to focus on growth.
          </p>
          <div className="newhome-feature-grid">
            {featureCards.map((feature) => (
              <article className="newhome-feature-card" key={feature.title}>
                <img src={feature.image} alt={feature.title} />
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="newhome-sourcing" ref={sourcingSectionRef} id="sourcing-request">
          <div className="newhome-sourcing-card">
            <div className="newhome-sourcing-copy">
              <h2 className="newhome-section-title">Sourcing Request</h2>
              <p className="newhome-section-subtitle">
                Need a specific product or customization? <br />
                Share the details and our sourcing specialists will follow up via email with tailored options.
              </p>
              <ul className="newhome-sourcing-bullets">
                <li>Describe the product specs, order volume, branding or customization ideas, shipping countries, etc. </li>
                <li>You can also paste any product page or listing links that show what you're looking for.</li>
                <li>Attach reference pictures, quotations, or documents.</li>
                <li>We will respond to your email as soon as possible.</li>
              </ul>
            </div>
            <form className="newhome-sourcing-form" onSubmit={handleSourcingSubmit}>
              <label className="newhome-input-label">
                Email
                <input
                  type="email"
                  className="newhome-input"
                  placeholder="you@example.com"
                  value={sourcingEmail}
                  onChange={(event) => setSourcingEmail(event.target.value)}
                />
              </label>
              <label className="newhome-input-label">
                Request Details
                <textarea
                  className="newhome-textarea"
                  rows={4}
                  placeholder={
                    "Please tell us about the products you want to source. For example:\n" +
                    "- quality, features, brand, etc.\n" +
                    "- Target price, quantity, timeline.\n" +
                    "- Branding or customization requirements.\n" +
                    "- Related product or listing links."
                  }
                  value={sourcingMessage}
                  onChange={(event) => setSourcingMessage(event.target.value)}
                />
              </label>
              <div className="newhome-attachment-input">
                <div className="newhome-input-label">Attachments (Optional)</div>
                <label className="newhome-file-label">
                  <input
                    type="file"
                    accept={ATTACHMENT_ACCEPT}
                    multiple
                    onChange={handleSourcingFilesChange}
                  />
                  <span>Select Files</span>
                </label>
                <p className="newhome-attachment-hint">
                  Supported file types: images and documents less than 3MB. <br />
                  (PDF, DOC, DOCX, PPT, PPTX, XLS, XLSX)
                </p>
                {sourcingFiles.length > 0 && (
                  <ul className="newhome-attachment-list">
                    {sourcingFiles.map((item) => (
                      <li key={item.uid}>
                        <div>
                          <span>{item.name}</span>
                          <span className="newhome-attachment-size">{formatFileSize(item.size)}</span>
                        </div>
                        <button type="button" onClick={() => handleRemoveSourcingFile(item.uid)}>
                          Remove
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <button className="newhome-button" type="submit" disabled={sourcingLoading}>
                {sourcingLoading ? 'Requesting...' : 'Request'}
              </button>
            </form>
          </div>
        </section>
        <Modal
          title="Request received"
          open={sourcingSuccessVisible}
          footer={null}
          closable={false}
        >
          <p>Thanks! We will reach out via email shortly.</p>
          <div style={{ textAlign: 'right', marginTop: 16 }}>
            <Button type="primary" onClick={() => setSourcingSuccessVisible(false)}>
              Got it
            </Button>
          </div>
        </Modal>

        <section className="newhome-connect">
          <div>
            <h2 className="newhome-section-title" id="pricing">Connect Your Stores in Minutes</h2>
            <p className="newhome-section-subtitle">
              Authorize the storefront you sell on, sync SKUs, inventory, and orders instantly, and deliver a consistent experience everywhere.
            </p>
            <select
              className="newhome-select"
              value={selectedStore}
              onChange={(event) => setSelectedStore(event.target.value)}
            >
              {storeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <button className="newhome-button newhome-button--wide" onClick={handleGoToMyStores}>
              Connect Now
            </button>
          </div>
          <img src="/images/newhome/connect-illustration.svg" alt="Connection illustration" />
        </section>

        <section>
          <h2 className="newhome-section-title">Curated Products Ready to Ship</h2>
          <p className="newhome-section-subtitle">
            Discover fast-moving items from vetted suppliers, backed by real-time stock data and flexible pricing strategies.
          </p>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '60px 0' }}>
              <div style={{
                display: 'inline-block',
                width: '40px',
                height: '40px',
                border: '4px solid #f3f3f3',
                borderTop: '4px solid #0c0c0c',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}>
              </div>
              <p style={{ marginTop: '16px', color: '#666' }}>Loading products...</p>
            </div>
          ) : (
            <div className="newhome-store-list">
              {products.map((product) => {
                const priceInfo = getProductPrice(product);
                const productDetailUrl = `/product/${product.id}`;
                return (
                  <article className="newhome-product-card" key={product.id}>
                    <a
                      href={productDetailUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ display: 'block' }}
                    >
                      <img
                        src={getProductImage(product)}
                        alt={product.name}
                        onError={(e) => {
                          e.target.src = '';
                        }}
                      />
                    </a>
                    <h4>
                      <a
                        href={productDetailUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: 'inherit', textDecoration: 'none' }}
                      >
                        {product.name}
                      </a>
                    </h4>
                    <div className="newhome-product-price">
                      <span>${priceInfo.price.toFixed(2)}</span>
                    </div>
                    <span className="newhome-badge">{product.category?.name}</span>
                  </article>
                );
              })}
            </div>
          )}
        </section>

        <section>
          <h2 className="newhome-section-title" id="stories">
            Customer Stories
          </h2>
          <p className="newhome-section-subtitle">
            From emerging DTC brands to seasoned marketplaces, teams rely on CH Dropshipping to modernize supply chains and keep momentum.
          </p>
          <div className="newhome-testimonials">
            {testimonials.map((testimonial) => (
              <article className="newhome-testimonial-card" key={testimonial.name}>
                <img src={testimonial.avatar} alt={testimonial.name} />
                <div className="newhome-testimonial-name">{testimonial.name}</div>
                <div className="newhome-testimonial-role">{testimonial.role}</div>
                <p className="newhome-testimonial-quote">{testimonial.quote}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="newhome-cta">
          <h2>Start The Business Today</h2>
          <button className="newhome-button" onClick={handleGoToRegister}>
            Sign Up Now
          </button>
          <p className="newhome-cta-text">
            <br />
            Contact us: <a href="mailto:contact@chdropshipping.com" style={{ color: 'white', textDecoration: 'underline' }}>contact@chdropshipping.com</a>
          </p>
        </section>

        <Footer />
      </div>
    </div>
  );
};

export default NewHome;
